import Swiper from 'swiper'

import CustomFadePlugin from '../utils/swiper-plugins/custom-fade-plugin'

import {
  HERO_SLIDER_CLASS,
  HERO_SLIDER_SLIDES_CLASS,
  HERO_SLIDER_LIST_CLASS,
  HERO_SLIDER_LIST_ITEM_CLASS,
  HERO_SLIDER_LIST_ITEM_NAVIGATION_TITLE_CLASS,
  HERO_SLIDER_LIST_ITEM_ACTIVE_CLASS,
  HERO_SLIDER_SLIDE_ACTIVE_CLASS,
  HERO_SLIDER_NEXT_BUTTON_CLASS,
  HERO_SLIDER_PREV_BUTTON_CLASS,
  HERO_SLIDER_PAGINATION_BULLET_CLASS,
  HERO_SLIDER_PAGINATION_BULLET_ACTIVE_CLASS,
} from '../constants/hero'

import { HIDDEN } from '../constants/a11y'

class HeroSlider {
  constructor() {
    this.heroSliderParent = document.querySelector(`.${HERO_SLIDER_CLASS}`)

    if (!this.heroSliderParent) return

    this.heroSliderSlidesParent = this.heroSliderParent.querySelector(
      `.${HERO_SLIDER_SLIDES_CLASS}`
    )
    this.heroSliderList = this.heroSliderParent.querySelector(`.${HERO_SLIDER_LIST_CLASS}`)
    this.heroSliderListItems = this.heroSliderList.querySelectorAll(
      `.${HERO_SLIDER_LIST_ITEM_CLASS}`
    )
    this.heroSliderListItemTitles = Array.from(this.heroSliderListItems).map(item =>
      item.querySelector(`.${HERO_SLIDER_LIST_ITEM_NAVIGATION_TITLE_CLASS}`)
    )

    this.heroSliderNextButton = this.heroSliderParent.querySelector(
      `.${HERO_SLIDER_NEXT_BUTTON_CLASS}`
    )
    this.heroSliderPreviousButton = this.heroSliderParent.querySelector(
      `.${HERO_SLIDER_PREV_BUTTON_CLASS}`
    )

    this.initSwiper()
    this.initListListeners()
  }

  /**
   * Initialize swiper
   */
  initSwiper() {
    this.swiper = new Swiper(this.heroSliderSlidesParent, {
      effect: 'custom-fade',
      init: false,
      slidesPerView: 1,
      slidesPerGroup: 1,
      watchSlidesProgress: true,
      spaceBetween: 0,
      preventInteractionOnTransition: true,
      simulateTouch: false,
      allowTouchMove: false,
      virtualTranslate: true,
      slideActiveClass: HERO_SLIDER_SLIDE_ACTIVE_CLASS,

      pagination: {
        el: '.hero-slider__slides-pagination',
        type: 'bullets',
        bulletClass: HERO_SLIDER_PAGINATION_BULLET_CLASS,
        bulletActiveClass: HERO_SLIDER_PAGINATION_BULLET_ACTIVE_CLASS,
      },
    })

    this.initSwiperListeners()
    this.initSwiperControlsListeners()
    this.swiper.init()
  }

  /**
   * Initialize swiper listeners
   */
  initSwiperListeners() {
    const { setTransition, setTranslate } = CustomFadePlugin({ swiper: this.swiper })

    this.swiper.on('setTransition', (_s, duration) => setTransition(duration))

    this.swiper.on('setTranslate', () => setTranslate())

    this.swiper.on('slideChange', () => {
      this.deactivateAllListItems(this.heroSliderListItems)
      this.activateListItem(this.heroSliderListItems[this.swiper.activeIndex])
    })
  }

  /**
   * Initialize list items listeners
   */
  initListListeners() {
    this.heroSliderListItemTitles.forEach((item, index) =>
      item.addEventListener('click', this.handleHeroListItemTitleClick.bind(this, index))
    )
  }

  /**
   * Initialize swiper control buttons listeners
   */
  initSwiperControlsListeners() {
    this.heroSliderPreviousButton.addEventListener('click', this.handlePrevButtonClick.bind(this))
    this.heroSliderNextButton.addEventListener('click', this.handleNextButtonClick.bind(this))
  }

  /**
   * Handle hero list item title element click
   * Gets index of list item and activates following slide
   * @param {Number} index
   * @param {Event} e
   * @returns {undefined}
   */
  handleHeroListItemTitleClick(index) {
    if (this.swiper.activeIndex === index) return

    const slideEl = this.swiper.slides.eq(index)

    this.swiper.activeIndex = index
    this.deactivateAllSlides(this.swiper.slides)
    this.activateSlide(slideEl[0])

    this.swiper.emit('slideChange')
  }

  /**
   * Handle "next slide" button click
   * @returns {undefined}
   */
  handleNextButtonClick() {
    if (this.swiper.slides.length - 1 == this.swiper.activeIndex) return

    this.swiper.activeIndex += 1
    const nextSlideEl = this.swiper.slides.eq(this.swiper.activeIndex)

    this.deactivateAllSlides(this.swiper.slides)
    this.activateSlide(nextSlideEl[0])
    this.togglePaginationBullets(this.swiper.activeIndex)

    this.swiper.emit('slideChange')
  }

  /**
   * Handle "previous slide" button click
   * @returns {undefined}
   */
  handlePrevButtonClick() {
    if (this.swiper.activeIndex === 0) return

    this.swiper.activeIndex -= 1
    const prevSlideEl = this.swiper.slides.eq(this.swiper.activeIndex)

    this.deactivateAllSlides(this.swiper.slides)
    this.activateSlide(prevSlideEl[0])
    this.togglePaginationBullets(this.swiper.activeIndex)

    this.swiper.emit('slideChange')
  }

  /**
   * Activate bullet by index and deactivate previous ones
   * @param {Number} index
   */
  togglePaginationBullets(index) {
    const activeBullet = this.swiper.pagination.bullets[index]

    this.deactivateAllPaginationBullets(this.swiper.pagination.bullets)
    this.activatePaginationBullet(activeBullet)
  }

  /**
   * Activate pagination bullet
   * @param {HTMLElement} bullet
   */
  activatePaginationBullet(bullet) {
    bullet.classList.add(HERO_SLIDER_PAGINATION_BULLET_ACTIVE_CLASS)
  }

  /**
   * Deactivate pagination bullet
   * @param {HTMLElement} bullet
   */
  deactivatePaginationBullet(bullet) {
    bullet.classList.remove(HERO_SLIDER_PAGINATION_BULLET_ACTIVE_CLASS)
  }

  /**
   * Deactivate all pagination bullets in given DOM7 array
   * @param {DOM7[]} bullets
   */
  deactivateAllPaginationBullets(bullets) {
    Array.from(bullets).map(bullet => this.deactivatePaginationBullet(bullet))
  }

  /**
   * Activate slide
   * @param {HTMLElement} slide
   */
  activateSlide(slide) {
    slide.classList.add(HERO_SLIDER_SLIDE_ACTIVE_CLASS)
    slide.setAttribute(HIDDEN, 'false')
  }

  /**
   * Deactivate slide
   * @param {HTMLElement} slide
   */
  deactivateSlide(slide) {
    slide.classList.remove(HERO_SLIDER_SLIDE_ACTIVE_CLASS)
    slide.setAttribute(HIDDEN, 'true')
  }

  /**
   * Deactivate all slides in given DOM7 array
   * @param {DOM7[]} slides
   */
  deactivateAllSlides(slides) {
    Array.from(slides).map(slide => this.deactivateSlide(slide))
  }

  /**
   * Activate list item
   * @param {HTMLElement} item
   */
  activateListItem(item) {
    item.classList.add(HERO_SLIDER_LIST_ITEM_ACTIVE_CLASS)
  }

  /**
   * Deactivate list item
   * @param {HTMLElement} item
   */
  deactivateListItem(item) {
    item.classList.remove(HERO_SLIDER_LIST_ITEM_ACTIVE_CLASS)
  }

  /**
   * Deactivate all list items in given NodeList array
   * @param {NodeList[]} items
   */
  deactivateAllListItems(items) {
    items.forEach(item => this.deactivateListItem(item))
  }

  /**
   * Find list item element by title element
   * @param {HTMLElement} titleEl
   * @returns {HTMLElement || null}
   */
  findListItemByTitle(titleEl) {
    return Array.from(this.heroSliderListItems).find(item => item.contains(titleEl))
  }
}

export default HeroSlider
